import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, User, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Am I paying the right rate for Shanghai → Sydney 40HC right now?",
  "Should I lock a contract rate or stay spot for the next 90 days?",
  "How do I avoid detention on a shipment landing Friday with no broker?",
  "Compare FOB vs EXW for a new supplier in Vietnam.",
  "Write a negotiation brief for our annual carrier tender.",
];

const MODELS = [
  { value: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5" },
  { value: "gpt-5.2", label: "GPT-5.2" },
];

export default function Consultant() {
  const [model, setModel] = useState(MODELS[0].value);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi. I'm your supply chain consultant. Ask me about freight rates, procurement strategy, detention avoidance, Incoterms, or invoice audit. I'll give you concrete next steps." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { message: content, session_id: sessionId, model });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      toast.error("AI consultant is unavailable. Check API key.");
      setMessages(m => [...m, { role: "assistant", content: "I couldn't reach the consultant service. Please retry." }]);
    }
    setBusy(false);
  };

  return (
    <div className="animate-slide-up" data-testid="consultant-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">AI Supply Chain Consultant</h1>
          <p className="text-sm text-zinc-500 mt-1">Senior procurement advice on demand. Powered by Claude Sonnet 4.5 and GPT-5.2.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="data-label">Model</span>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="model-select" className="rounded-none w-[200px] h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {MODELS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Suggestions */}
        <aside className="bg-[#F4F4F5] border border-zinc-200 p-5 lg:col-span-1">
          <div className="data-label mb-3 flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#002FA7]" /> Try asking</div>
          <div className="space-y-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                data-testid={`suggestion-${i}`}
                onClick={() => send(s)}
                disabled={busy}
                className="w-full text-left text-xs text-zinc-700 bg-white border border-zinc-200 px-3 py-2 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <div className="lg:col-span-3 bg-white border border-zinc-200 flex flex-col min-h-[640px]">
          <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-5" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-zinc-900 text-white' : 'bg-[#002FA7] text-white'}`}>
                  {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed border ${m.role === 'user' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-[#FAFAFA] border-zinc-200 text-zinc-900'}`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#002FA7] text-white"><Bot className="w-4 h-4" /></div>
                <div className="px-4 py-3 bg-[#FAFAFA] border border-zinc-200 text-sm text-zinc-500">
                  <span className="inline-block w-1.5 h-1.5 bg-zinc-400 mr-1 animate-pulse-dot" />
                  <span className="inline-block w-1.5 h-1.5 bg-zinc-400 mr-1 animate-pulse-dot" style={{ animationDelay: '0.2s' }} />
                  <span className="inline-block w-1.5 h-1.5 bg-zinc-400 animate-pulse-dot" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-zinc-200 p-4 flex gap-2">
            <Textarea
              data-testid="chat-input"
              placeholder="Ask about rates, surcharges, Incoterms, detention, invoice variance..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              className="rounded-none resize-none min-h-[48px] max-h-32"
              rows={1}
            />
            <Button
              data-testid="chat-send"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-12 px-5"
            ><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
